<template>
  <div class="fill-height">
    <line-chart
      v-if="viewMode === 'chart'"
      class="fill-height"
      :raw-data="removable ? statsMock : stats"
      :chart-value-keys="currentChartValueKeys"
      x-value="date"
      y-value-prefix="stats."
      :yaxis="[{ title: { text: currentChartTitle } }, ...(displayMode === 'loads') ? [{ title: { text: 'Training Grade' }, opposite: true, seriesName: 'TG_DL', min: 0, max: 10}] : []]"
      :fill="displayMode === 'tl' ? { type: ['gradient'], opacity: [0.25], gradient: { inverseColors: false, shade: 'light', type: 'vertical', opacityFrom: 0.85, opacityTo: 0.55 } } : { type: ['solid'], opacity: [1]}"
      :annotations="{xaxis: xAxisAnnotations}"
    >
      <template v-slot:header>
        <v-card-title class="pa-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <div class="subtitle-2 grey--text text--grey-100">
                {{ currentChartTitle }}
              </div>
            </v-col>
            <v-col
              v-if="removable"
              cols="auto"
            >
              <v-btn
                icon
                style="pointer-events: auto"
                @click="$emit('remove')"
              >
                <v-icon small>
                  fal fa-times
                </v-icon>
              </v-btn>
            </v-col>
            <v-col
              v-else
              cols="auto"
              class="text-right"
            >
              <div class="d-flex align-center">
                <visual-list-toggle
                  :value.sync="viewMode"
                  class="mr-3"
                />
                <v-btn-toggle
                  v-model="displayMode"
                  mandatory
                  dense
                  color="primary"
                  borderless
                >
                  <v-btn
                    small
                    value="loads"
                  >
                    TTM/TG
                  </v-btn>
                  <v-btn
                    small
                    value="tl"
                  >
                    TL
                  </v-btn>
                </v-btn-toggle>
              </div>
            </v-col>
          </v-row>
        </v-card-title>
      </template>
    </line-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-4">
        <v-row
          no-gutters
          align="center"
        >
          <v-col>
            <div class="subtitle-2 grey--text text--grey-100">
              {{ currentChartTitle }}
            </div>
          </v-col>
          <v-col
            cols="auto"
            class="text-right"
          >
            <div class="d-flex align-center">
              <visual-list-toggle
                v-if="!removable"
                :value.sync="viewMode"
                class="mr-3"
              />
              <v-btn-toggle
                v-model="displayMode"
                mandatory
                dense
                color="primary"
                borderless
              >
                <v-btn
                  small
                  value="loads"
                >
                  TTM/TG
                </v-btn>
                <v-btn
                  small
                  value="tl"
                >
                  TL
                </v-btn>
              </v-btn-toggle>
            </div>
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table
        :headers="tableHeaders"
        :items="tableData"
        hide-default-footer
        disable-pagination
        :items-per-page="-1"
        fixed-header
        dense
        :height="350"
      >
        <template v-slot:item.date="{ item }">
          {{ $dayjs(item.date).format('ll - ddd') }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'

export default {
  name: 'TrainingLoadChart',
  components: { LineChart, VisualListToggle },
  props: {
    stats: {
      type: Array,
      required: true
    },
    currentDate: {
      type: Object,
      default: () => null
    },
    removable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      displayMode: 'loads',
      viewMode: 'chart'
    }
  },
  computed: {
    currentChartValueKeys() {
      if (this.displayMode === 'loads') {
        return [
          { key: 'TTM_DL', type: 'line', label: 'Total Training Time' },
          { key: 'TG_DL', type: 'line', label: 'Training Grade' }
        ]
      } else {
        return [
          { key: 'TL_DL', type: 'area', label: 'Training Load' }
        ]
      }
    },
    currentChartTitle() {
      return this.displayMode === 'loads' ? 'Total Training Time & Training Grade' : 'Training Load'
    },
    xAxisAnnotations() {
      const currentDate = this.currentDate

      if (!currentDate) return []
      const annotations = []

      for (let i = 0; i < 29; i++) {
        const checkDate = currentDate.clone().subtract(i, 'day')
        if (checkDate.day() === 0) {
          annotations.push({
            x: checkDate.valueOf()
          })
        }
      }

      return annotations
    },
    statsMock() {
      return this.stats.map(stat => ({
        date: stat.start_at,
        stats: {
          TTM_DL: Math.round(Math.random() * 100),
          TG_DL: Math.round(Math.random() * 10),
          TL_DL: Math.round(Math.random() * 150)
        }
      }))
    },
    tableData() {
      const data = this.removable ? this.statsMock : this.stats
      return data.map(item => {
        const stats = item.stats || {}
        if (this.displayMode === 'loads') {
          return {
            date: item.date,
            ttm: stats.TTM_DL || 0,
            tg: stats.TG_DL || 0
          }
        } else {
          return {
            date: item.date,
            tl: stats.TL_DL || 0
          }
        }
      })
    },
    tableHeaders() {
      const baseHeaders = [
        {
          text: this.$t('table_headers.date'),
          value: 'date',
          sortable: true,
          width: '30%'
        }
      ]

      if (this.displayMode === 'loads') {
        return [
          ...baseHeaders,
          {
            text: this.$t('table_headers.total_training_time'),
            value: 'ttm',
            sortable: true,
            align: 'center'
          },
          {
            text: this.$t('table_headers.training_grade'),
            value: 'tg',
            sortable: true,
            align: 'center'
          }
        ]
      } else {
        return [
          ...baseHeaders,
          {
            text: this.$t('table_headers.training_load'),
            value: 'tl',
            sortable: true,
            align: 'center'
          }
        ]
      }
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString()
    }
  }
}
</script>
